
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Plus, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface UserGroupsProps {
  searchQuery: string;
}

const UserGroups = ({ searchQuery }: UserGroupsProps) => {
  // Mock data - would typically come from an API
  const groups = [
    { id: 1, name: 'Administrators', members: 1, description: 'Full access to all systems', permissions: ['read', 'write', 'delete', 'manage_users'] },
    { id: 2, name: 'Content Managers', members: 3, description: 'Can manage all content', permissions: ['read', 'write', 'publish'] },
    { id: 3, name: 'Marketing Team', members: 2, description: 'Access to marketing tools and analytics', permissions: ['read', 'write', 'analytics'] },
    { id: 4, name: 'Viewers', members: 10, description: 'Read-only access to content', permissions: ['read'] },
  ];
  
  const filteredGroups = searchQuery 
    ? groups.filter(group => 
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : groups;
  
  return (
    <div>
      <div className="mb-6">
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Group
        </Button>
      </div>
      
      {filteredGroups.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredGroups.map((group) => (
            <Card key={group.id}>
              <CardHeader className="pb-3">
                <div className="flex justify-between">
                  <div>
                    <CardTitle className="text-lg">{group.name}</CardTitle>
                    <CardDescription>{group.description}</CardDescription>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Users className="h-4 w-4 mr-1" />
                    <span>{group.members} members</span>
                  </div>
                  <Button variant="outline" size="small">Manage Members</Button>
                </div>
                
                <div>
                  <p className="text-sm font-medium mb-2">Permissions:</p>
                  <div className="flex flex-wrap gap-2">
                    {group.permissions.map((permission, index) => (
                      <Badge key={index} variant="outline">
                        {permission.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-medium">No groups found</h3>
          <p className="text-sm text-muted-foreground">Create a new group or try a different search</p>
        </div>
      )}
    </div>
  );
};

export default UserGroups;
